import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { ToastProvider } from './contexts/ToastContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MainLayout } from './components/layout/MainLayout';
import { ErrorBoundary, RouteErrorBoundary } from './components/ui/ErrorBoundary';
import { OfflineDetector } from './components/ui/OfflineDetector';
import { NotificationContainer } from './components/ui/NotificationContainer';
import { SkipToContent } from './components/ui/SkipToContent';
import { AnnouncerProvider } from './components/ui/ScreenReaderAnnouncer';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { ServiceWorkerStatus } from './components/ui/ServiceWorkerStatus';
import { queryClient } from './api/queryClient';

// Lazy load page components for code splitting
const AuthPage = lazy(() => import('./pages/AuthPage').then(module => ({ default: module.AuthPage })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const CreateSession = lazy(() => import('./pages/CreateSession').then(module => ({ default: module.CreateSession })));
const SessionsList = lazy(() => import('./pages/SessionsList').then(module => ({ default: module.SessionsList })));
const SessionDetail = lazy(() => import('./pages/SessionDetail').then(module => ({ default: module.SessionDetail })));
const UserProfile = lazy(() => import('./pages/UserProfile').then(module => ({ default: module.UserProfile })));
const Analytics = lazy(() => import('./pages/Analytics').then(module => ({ default: module.Analytics })));

// Game management pages
const GamesList = lazy(() => import('./pages/GamesList'));
const CreateGame = lazy(() => import('./pages/CreateGame'));
const EditGame = lazy(() => import('./pages/EditGame'));
const GameDetail = lazy(() => import('./pages/GameDetail'));
const EndGame = lazy(() => import('./pages/EndGame'));

// Email invitation pages
import RSVPPage from './components/games/RSVPPage';

// Pending session review page
const PendingSessionReview = lazy(() => import('./pages/PendingSessionReview').then(module => ({ default: module.PendingSessionReview })));

// Loading fallback component for lazy routes
const RouteLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="lg" text="Loading page..." />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <NotificationProvider>
            <AccessibilityProvider>
              <ToastProvider>
                <AnnouncerProvider>
                  <OfflineDetector>
                    <Router>
                      <SkipToContent contentId="main-content" />
                      <NotificationContainer />
                    <ServiceWorkerStatus />
                    <Routes>
                {/* Public routes */}
                <Route path="/auth" element={
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <RouteErrorBoundary>
                      <AuthPage />
                    </RouteErrorBoundary>
                  </Suspense>
                } />
                
                {/* RSVP route - public so users can respond without logging in */}
                <Route path="/rsvp/:invitationId" element={<RSVPPage />} />
                
                {/* Protected routes with layout */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <Dashboard />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Sessions routes */}
                <Route 
                  path="/sessions" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <SessionsList />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/sessions/new" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <CreateSession />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/sessions/:sessionId" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <SessionDetail />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* Pending session review route */}
                <Route 
                  path="/pending-sessions/:sessionId" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <PendingSessionReview />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* Games routes */}
                <Route 
                  path="/games" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <GamesList />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/games/create" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <CreateGame />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/games/:gameId" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <GameDetail />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/games/:gameId/edit" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <EditGame />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/games/:gameId/end" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <EndGame />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/analytics" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <RouteErrorBoundary>
                          <Suspense fallback={<LoadingSpinner size="lg" text="Loading analytics..." />}>
                            <Analytics />
                          </Suspense>
                        </RouteErrorBoundary>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* User Profile route */}
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={<RouteLoadingFallback />}>
                          <RouteErrorBoundary>
                            <UserProfile />
                          </RouteErrorBoundary>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                
                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
          </OfflineDetector>
        </AnnouncerProvider>
      </ToastProvider>
    </AccessibilityProvider>
  </NotificationProvider>
</AuthProvider>
</QueryClientProvider>
</ErrorBoundary>
  );
}

export default App;
